import { useState, useRef, useCallback, type KeyboardEvent } from 'react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

/**
 * Reusable date picker component with excellent UX for accounting users.
 *
 * Features:
 * - Click the calendar icon to open a calendar popup
 * - Type dates directly with keyboard (YYYY-MM-DD or DD-MM-YYYY)
 * - Navigate months/years with arrow buttons or keyboard
 * - Consistent cross-browser appearance
 * - Styled to match the project's Tailwind design system
 *
 * All dates are stored and communicated as YYYY-MM-DD strings (ISO format).
 */

interface DatePickerInputProps {
  /** Current date value as YYYY-MM-DD string. Empty string means no date selected. */
  value: string;
  /** Called when the user selects or types a date. Receives YYYY-MM-DD string or empty string. */
  onChange: (value: string) => void;
  /** Label text shown above the input (optional, for standalone use) */
  label?: string;
  /** Placeholder text when no date is selected */
  placeholder?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Minimum selectable date as YYYY-MM-DD */
  minDate?: string;
  /** Maximum selectable date as YYYY-MM-DD */
  maxDate?: string;
  /** Additional CSS class for the wrapper div */
  className?: string;
  /** Additional CSS class for the input element */
  inputClassName?: string;
  /** ID for the input element */
  id?: string;
  /** Accessible name for the input */
  'aria-label'?: string;
  /** AI field name for data-ai-field attribute (used by AI copilot to fill fields) */
  dataAiField?: string;
}

/**
 * Parse a YYYY-MM-DD string into a Date object (local time, not UTC).
 * Returns null if the string is empty or invalid.
 */
function parseLocalDate(yyyyMmDd: string): Date | null {
  if (!yyyyMmDd) return null;
  const parts = yyyyMmDd.split('-');
  if (parts.length !== 3) return null;
  const year = parseInt(parts[0]!, 10);
  const month = parseInt(parts[1]!, 10);
  const day = parseInt(parts[2]!, 10);
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
  // JavaScript months are 0-indexed
  const d = new Date(year, month - 1, day);
  // Verify the date didn't roll over (e.g., Feb 30 -> Mar 2)
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) {
    return null;
  }
  return d;
}

/**
 * Format a Date object to YYYY-MM-DD string.
 */
function formatToLocalDate(d: Date | null): string {
  if (!d) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Try to parse a typed date string (YYYY-MM-DD or DD-MM-YYYY).
 * Returns null if the string doesn't match or is an invalid date.
 */
function tryParseDate(text: string): Date | null {
  const isoMatch = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  const euMatch = text.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);

  let y: number = 0, m: number = 0, d: number = 0;
  if (isoMatch) {
    y = Number(isoMatch[1]);
    m = Number(isoMatch[2]);
    d = Number(isoMatch[3]);
  } else if (euMatch) {
    d = Number(euMatch[1]);
    m = Number(euMatch[2]);
    y = Number(euMatch[3]);
  } else {
    return null;
  }

  const date = new Date(y, m - 1, d);
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) {
    return null;
  }
  return date;
}

export default function DatePickerInput({
  value,
  onChange,
  label,
  placeholder = 'YYYY-MM-DD',
  required = false,
  disabled = false,
  minDate: minDateStr,
  maxDate: maxDateStr,
  className = '',
  inputClassName = '',
  id,
  'aria-label': ariaLabel,
  dataAiField,
}: DatePickerInputProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [rawInput, setRawInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedDate = parseLocalDate(value);
  const minDate = parseLocalDate(minDateStr ?? '');
  const maxDate = parseLocalDate(maxDateStr ?? '');

  const handleChange = useCallback(
    (date: Date | null) => {
      onChange(formatToLocalDate(date));
      setIsOpen(false);
      setIsTyping(false);
    },
    [onChange]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setRawInput(e.target.value);
      setIsTyping(true);
    },
    []
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (isTyping) {
          const parsed = tryParseDate(rawInput);
          if (parsed) {
            onChange(formatToLocalDate(parsed));
          } else if (rawInput === '') {
            onChange('');
          }
          setIsTyping(false);
        }
        setIsOpen(false);
      } else if (e.key === 'Escape') {
        setIsOpen(false);
        setIsTyping(false);
        setRawInput('');
      }
    },
    [isTyping, rawInput, onChange]
  );

  const handleBlur = useCallback(() => {
    if (isTyping) {
      // Revert to the committed value — user didn't press Enter
      setIsTyping(false);
      setRawInput('');
    }
  }, [isTyping]);

  const handleFocus = useCallback(() => {
    if (!isOpen && !disabled) {
      // Prepare raw input from current value
      if (value) {
        setRawInput(value);
      }
    }
  }, [isOpen, disabled, value]);

  const displayValue = isTyping ? rawInput : value;

  return (
    <div className={`date-picker-wrapper ${className}`}>
      {label && (
        <label className="block text-xs text-gray-500 mb-1">
          {label}
        </label>
      )}
      <div className="relative date-picker-container">
        <ReactDatePicker
          selected={selectedDate}
          onChange={handleChange}
          minDate={minDate ?? undefined}
          maxDate={maxDate ?? undefined}
          open={isOpen}
          onInputClick={() => setIsOpen(true)}
          onClickOutside={() => {
            setIsOpen(false);
            setIsTyping(false);
          }}
          onSelect={() => setIsOpen(false)}
          customInput={
            <input
              ref={inputRef}
              id={id}
              {...(dataAiField ? { 'data-ai-field': dataAiField } : {})}
              type="text"
              className={`w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white
                focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                disabled:bg-gray-100 disabled:text-gray-400
                ${inputClassName}`}
              placeholder={placeholder}
              value={displayValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              onFocus={handleFocus}
              required={required}
              disabled={disabled}
              aria-label={ariaLabel ?? label ?? 'Date'}
              autoComplete="off"
            />
          }
          dateFormat="yyyy-MM-dd"
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          isClearable={!required}
          showPopperArrow={false}
          popperPlacement="bottom-start"
          portalId="date-picker-portal"
        />
        {/* Calendar toggle button */}
        <button
          type="button"
          className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
          onClick={() => {
            if (!disabled) {
              setIsOpen((prev) => !prev);
              inputRef.current?.focus();
            }
          }}
          tabIndex={-1}
          aria-label="Open calendar"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="1" y="3" width="14" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" />
            <path d="M1 7h14" stroke="currentColor" strokeWidth="1.5" />
            <path d="M5 1v4M11 1v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}
